/**
 * ImageCropDialog
 *
 * Generic image crop/rotate dialog built on react-easy-crop.
 * Module-agnostic — the consumer controls open state, labels, and what to do with the resulting Blob.
 *
 * @example
 * <ImageCropDialog
 *   open={cropOpen}
 *   imageSrc={previewUrl}
 *   cropShape="round"
 *   onConfirm={(blob) => uploadAvatar.mutate(blob)}
 *   onCancel={() => setCropOpen(false)}
 *   labels={{ title: t('crop.title'), confirm: t('crop.confirm'), cancel: t('common.cancel') }}
 * />
 */

import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { RotateCcw, RotateCw } from 'lucide-react';

import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Slider } from '@/shared/components/ui/slider';
import { getCroppedBlob } from './useCropImage';

/* ── Types ──────────────────────────────────────────────────── */

export interface ImageCropDialogLabels {
  title?: string;
  zoom?: string;
  rotate?: string;
  confirm?: string;
  cancel?: string;
}

export interface ImageCropDialogProps {
  open: boolean;
  /** Object URL or data URL of the source image. Pass null to keep dialog unmounted. */
  imageSrc: string | null;
  /** Crop aspect ratio. Default: 1 (square) */
  aspect?: number;
  /** Shape of the crop area overlay. Default: 'round' */
  cropShape?: 'rect' | 'round';
  /** Show zoom slider. Default: true */
  showZoom?: boolean;
  /** Show rotate controls. Default: true */
  showRotate?: boolean;
  /** Output MIME type. Default: 'image/jpeg' */
  mimeType?: 'image/jpeg' | 'image/png' | 'image/webp';
  /** JPEG/WebP compression quality 0–1. Default: 0.92 */
  quality?: number;
  /** Called with the cropped Blob when the user confirms */
  onConfirm: (blob: Blob) => void;
  /** Called when the user cancels or closes the dialog */
  onCancel: () => void;
  /** Translated labels. Falls back to English defaults. */
  labels?: ImageCropDialogLabels;
}

/* ── Constants ──────────────────────────────────────────────── */

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.05;
const ROTATION_STEP = 90;

/* ── Component ──────────────────────────────────────────────── */

export function ImageCropDialog({
  open,
  imageSrc,
  aspect = 1,
  cropShape = 'round',
  showZoom = true,
  showRotate = true,
  mimeType = 'image/jpeg',
  quality = 0.92,
  onConfirm,
  onCancel,
  labels = {},
}: ImageCropDialogProps) {
  const {
    title = 'Adjust photo',
    zoom: zoomLabel = 'Zoom',
    rotate: rotateLabel = 'Rotate',
    confirm = 'Apply',
    cancel = 'Cancel',
  } = labels;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleRotate = (direction: 'ccw' | 'cw') => {
    setRotation((prev) => {
      const next = direction === 'cw' ? prev + ROTATION_STEP : prev - ROTATION_STEP;
      return ((next % 360) + 360) % 360;
    });
  };

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, rotation, mimeType, quality);
      onConfirm(blob);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onCancel();
  };

  // Reset state when a new image is loaded
  const handleCropperMediaLoaded = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(ZOOM_MIN);
    setRotation(0);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md gap-0 p-0 overflow-hidden" showCloseButton={false}>
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* Crop area */}
        {imageSrc && (
          <div className="relative h-72 w-full bg-black/90">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              cropShape={cropShape}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              onMediaLoaded={handleCropperMediaLoaded}
            />
          </div>
        )}

        {/* Controls */}
        <div className="px-6 py-4 space-y-4">
          {showZoom && (
            <div className="space-y-2">
              <span className="text-xs font-medium text-muted-foreground">{zoomLabel}</span>
              <Slider
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={ZOOM_STEP}
                value={[zoom]}
                onValueChange={([value]) => setZoom(value)}
                aria-label={zoomLabel}
              />
            </div>
          )}

          {showRotate && (
            <div className="space-y-2">
              <span className="text-xs font-medium text-muted-foreground">{rotateLabel}</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleRotate('ccw')}
                  aria-label="Rotate counter-clockwise"
                >
                  <RotateCcw className="size-4" />
                </Button>
                <span className="flex-1 text-center text-sm tabular-nums text-muted-foreground">
                  {rotation}°
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleRotate('cw')}
                  aria-label="Rotate clockwise"
                >
                  <RotateCw className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 pb-6">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isProcessing}>
            {cancel}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isProcessing || !croppedAreaPixels}
          >
            {isProcessing ? '…' : confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
